/**
 * The questions of a Listening part or a Reading section, numbered like the real test
 * (« Questions 11–15 »), each answer saved on the server as soon as it is given (words after a
 * short pause). Shared by the Listening and Reading players.
 */

import { ApiError, academyAttemptsAnswerCreate } from "@agencyos/api-client";
import { type Item, type ItemResponse, hasPictures, initialResponse, isEmptyResponse } from "@agencyos/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExamPictureView, PictureOptions } from "../ExamPicture";
import { ItemInput } from "./ItemInput";
import { MethodLink } from "./MethodLink";
import { useContentLang } from "./contentLang";

export type SittingQuestion = Item & { response: ItemResponse };
type SaveState = "idle" | "saving" | "saved" | "error";

/** Local answers (winning over the server copy while a save is on its way) and their saving. */
export function useAnswers(attemptId: string, onClosed: () => Promise<void>) {
  const [answers, setAnswers] = useState<Record<string, ItemResponse>>({});
  const [saves, setSaves] = useState<Record<string, SaveState>>({});
  const timers = useRef(new Map<string, number>());

  const send = useCallback(
    async (questionId: string, response: ItemResponse) => {
      setSaves((s) => ({ ...s, [questionId]: "saving" }));
      try {
        await academyAttemptsAnswerCreate(attemptId, { question: questionId, response });
        setSaves((s) => ({ ...s, [questionId]: "saved" }));
      } catch (failure) {
        setSaves((s) => ({ ...s, [questionId]: "error" }));
        // Time up or part closed on the server: the new state decides what comes next.
        if (failure instanceof ApiError && failure.status === 400) await onClosed();
      }
    },
    [attemptId, onClosed],
  );

  const set = useCallback(
    (question: SittingQuestion, response: ItemResponse) => {
      setAnswers((prev) => ({ ...prev, [question.id]: response }));
      window.clearTimeout(timers.current.get(question.id));
      const typed = question.type === "gap" || question.type === "short";
      if (typed) {
        timers.current.set(
          question.id,
          window.setTimeout(() => {
            timers.current.delete(question.id);
            void send(question.id, response);
          }, 700),
        );
      } else {
        void send(question.id, response);
      }
    },
    [send],
  );

  /** Send at once whatever is still waiting (before moving on). */
  const flush = useCallback(
    async (questions: SittingQuestion[]) => {
      const waiting = questions.filter((q) => timers.current.has(q.id));
      await Promise.all(
        waiting.map((q) => {
          window.clearTimeout(timers.current.get(q.id));
          timers.current.delete(q.id);
          return send(q.id, answers[q.id] ?? q.response);
        }),
      );
    },
    [answers, send],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const valueOf = (q: SittingQuestion): ItemResponse => (q.id in answers ? (answers[q.id] ?? null) : (q.response ?? initialResponse(q)));
  const isAnswered = (q: SittingQuestion) => !isEmptyResponse(q, q.id in answers ? (answers[q.id] ?? null) : q.response);
  return { set, flush, valueOf, isAnswered, saves };
}

export function numberLabel(question: Item, fallback: number): { text: string; first: number } {
  const first = question.number ?? fallback;
  const list = question.numbers.length > 1 ? question.numbers : [];
  const last = list.length ? (list[list.length - 1] ?? first) : question.marks > 1 ? first + question.marks - 1 : first;
  return { text: last > first ? `${first}–${last}` : String(first), first };
}

export function QuestionBlock({
  question,
  fallbackNumber,
  value,
  onChange,
  saveState,
  disabled = false,
  skill = "",
  exam = "ielts_general",
  docType = "",
  rubric = [],
}: {
  question: SittingQuestion;
  fallbackNumber: number;
  value: ItemResponse;
  onChange: (value: ItemResponse) => void;
  saveState?: SaveState;
  disabled?: boolean;
  /** co or ce: shows « How to succeed in this type of question? » (D-107). */
  skill?: string;
  /** The exam of the sitting and, for the TEF, the type of the document (D-109). */
  exam?: string;
  docType?: string;
  /** The official rubric of the group that starts with this question (D-136), as on the test. */
  rubric?: readonly string[];
}) {
  const { t } = useTranslation();
  const { text, first } = numberLabel(question, fallbackNumber);
  const contentLang = useContentLang(); // French for the TEF (D-109)
  const many = text.includes("–");
  const titleId = `iq-${question.id}`;
  return (
    <li className="ac-iq" id={`iq-anchor-${first}`}>
      {rubric.map((line) => (
        <p key={line} className="ac-item__instructions ac-iq__rubric" lang={contentLang}>
          {line}
        </p>
      ))}
      <div className="ac-iq__head">
        <span className="ac-iq__num ao-num">{many ? t("ielts.player.questionsN", { n: text }) : t("ielts.player.questionN", { n: text })}</span>
        {saveState === "saving" && (
          <span className="ac-save-state">
            <span className="ac-dot ac-dot--busy" aria-hidden="true" />
            {t("academy.navigator.saving")}
          </span>
        )}
        {saveState === "error" && <span className="ac-footnote ko">{t("academy.player.networkError")}</span>}
        {skill && <MethodLink exam={exam} skill={skill} type={exam === "tef_canada" ? docType : question.type} newTab compact />}
      </div>
      <ExamPictureView picture={question.image} lang={contentLang} size="medium" />
      {question.type === "gap" ? (
        <h3 id={titleId} className="visually-hidden">
          {t("ielts.player.questionN", { n: text })}
        </h3>
      ) : (
        <h3 id={titleId} className="ac-stem ac-stem--small" lang={contentLang}>
          {question.prompt}
        </h3>
      )}
      {question.type === "mcq" && hasPictures(question.optionImages) ? (
        // TEF CO section 1: the four drawings « Dessin A » … « Dessin D » (2×2), radio semantics.
        <PictureOptions
          name={`iq-opt-${question.id}`}
          options={question.options}
          images={question.optionImages}
          value={typeof value === "number" ? value : null}
          onChange={(index) => onChange(index)}
          disabled={disabled}
          lang={contentLang}
          legend={question.prompt || t("academy.picture.choose")}
        />
      ) : (
        <ItemInput item={question} value={value} onChange={onChange} disabled={disabled} labelledBy={titleId} />
      )}
    </li>
  );
}
